import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiBarChart2 } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { isParentAccount } from '../mymod/accountRole';
import AttendanceCalendar from './components/AttendanceCalendar';
import LearningDetails from './components/LearningDetails';
import ReportInsights from './components/ReportInsights';
import ReportToolbar from './components/ReportToolbar';
import SummaryGrid from './components/SummaryGrid';
import { fetchReportChildren, fetchReportClasses, fetchReportRows } from './reportApi';
import {
  getPeriodRange,
  getPeriodTitle,
  moveAnchorDate,
  normalizeReportRows,
  parseDate,
  summarizeReport,
} from './reportUtils';
import './LmsLearningReport.scss';

const LmsLearningReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isParent = isParentAccount();
  const requestedClassCode = location.state?.classCode;
  const [mode, setMode] = useState('day');
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [classes, setClasses] = useState([]);
  const [classCode, setClassCode] = useState('');
  const [rows, setRows] = useState([]);
  const [children, setChildren] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [childrenLoading, setChildrenLoading] = useState(isParent);
  const [classLoading, setClassLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [childrenError, setChildrenError] = useState('');
  const [classError, setClassError] = useState('');
  const [reportError, setReportError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const selectedChild = children.find((child) => String(child.studentId) === studentId);
  const error = childrenError || classError || reportError;

  const range = useMemo(() => getPeriodRange(mode, anchorDate), [mode, anchorDate]);
  const summary = useMemo(() => summarizeReport(rows), [rows]);
  const periodTitle = useMemo(() => getPeriodTitle(mode, range), [mode, range]);

  useEffect(() => {
    if (!isParent) return undefined;
    let active = true;
    setChildrenLoading(true);
    setChildrenError('');
    setChildren([]);
    setStudentId('');
    fetchReportChildren().then((items) => {
      if (!active) return;
      setChildren(items);
      setStudentId(String(items[0]?.studentId || ''));
    }).catch((requestError) => {
      if (active) setChildrenError(requestError.message);
    }).finally(() => {
      if (active) setChildrenLoading(false);
    });
    return () => { active = false; };
  }, [isParent, retryCount]);

  useEffect(() => {
    let active = true;
    setClasses([]);
    setClassCode('');
    setRows([]);
    setClassError('');
    if (isParent && !studentId) {
      setClassLoading(false);
      return undefined;
    }
    setClassLoading(true);
    fetchReportClasses({ isParent, studentId }).then((items) => {
      if (!active) return;
      const uniqueClasses = Array.from(new Map(items.map((item) => [item.clClassCode, item])).values());
      const requestedClass = uniqueClasses.find((item) => String(item.clClassCode) === String(requestedClassCode));
      setClasses(uniqueClasses);
      setClassCode(String(requestedClass?.clClassCode || uniqueClasses[0]?.clClassCode || ''));
    }).catch((requestError) => {
      if (active) setClassError(requestError.message);
    }).finally(() => {
      if (active) setClassLoading(false);
    });
    return () => { active = false; };
  }, [isParent, studentId, requestedClassCode, retryCount]);

  useEffect(() => {
    let active = true;
    setRows([]);
    setReportError('');
    if (!classCode || classLoading || (isParent && !studentId)) {
      setReportLoading(false);
      return undefined;
    }
    setReportLoading(true);
    fetchReportRows({ isParent, studentId, classCode, startDate: range.startDate, endDate: range.endDate })
      .then((items) => {
        if (active) setRows(normalizeReportRows(items));
      }).catch((requestError) => {
        if (active) setReportError(requestError.message);
      }).finally(() => {
        if (active) setReportLoading(false);
      });
    return () => { active = false; };
  }, [isParent, studentId, classCode, classLoading, range.endDate, range.startDate, retryCount]);

  const handleChildChange = (value) => {
    // Never show the previous child's classes or results under the new child's name.
    setClasses([]);
    setClassCode('');
    setRows([]);
    setStudentId(value);
  };

  const handleAnchorDateChange = (value) => {
    if (value) setAnchorDate(parseDate(value));
  };

  return (
    <main className="lms-report" aria-label={isParent ? '자녀 학습 리포트' : '학습 리포트'}>
      <header className="lms-report-topbar">
        <div className="lms-report-topbar__inner">
          <button type="button" onClick={() => navigate(isParent ? '/menu' : '/lms')}
            aria-label={isParent ? '메뉴로 돌아가기' : '학습 화면으로 돌아가기'}>
            <FiArrowLeft aria-hidden="true" />
          </button>
          <div className="lms-report-brand">
            <span><FiBarChart2 aria-hidden="true" /></span>
            <div><strong>{isParent ? '자녀 학습 리포트' : '학습 리포트'}</strong></div>
          </div>
        </div>
      </header>

      <div className="lms-report__scroll">
        <div className="lms-report__container">
          <header className="lms-report__header">
            <div><h1>{isParent ? '자녀의 학습 상태' : '나의 학습 상태'}</h1>
              <span>{isParent ? '자녀를 선택해 출석·진도·과제·시험 결과를 확인하세요.'
                : '진도가 등록된 날짜를 수업일로 하여 출석·진도·과제·시험을 보여줍니다.'}</span></div>
          </header>

          {isParent && childrenLoading && <div className="lms-report__empty" role="status">연결된 자녀를 불러오는 중입니다.</div>}
          {isParent && !childrenLoading && !!children.length && (
            <section className="lms-report__children" aria-label="조회할 자녀 선택">
              <div className="lms-report__class-row">
                <label htmlFor="lms-report-child">자녀 선택</label>
                <select id="lms-report-child" value={studentId} onChange={(event) => handleChildChange(event.target.value)}>
                  {children.map((child) => (
                    <option key={child.studentId} value={child.studentId}>
                      {child.name || '학생'}{children.some((other) => other.studentId !== child.studentId && other.name === child.name)
                        ? ` (${child.stCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <p>{selectedChild?.name || '자녀'} 학생의 학습 기록을 조회하고 있습니다. 학습 수행과 AI 이용은 학생 계정에서 할 수 있습니다.</p>
            </section>
          )}
          {isParent && !childrenLoading && !children.length && !childrenError && (
            <div className="lms-report__empty">연결된 자녀가 없습니다. 학원에 학부모 휴대전화 등록 정보를 확인해 주세요.</div>
          )}

          {!classLoading && !!classes.length && (
            <ReportToolbar mode={mode} onModeChange={setMode} anchorDate={anchorDate}
              onAnchorDateChange={handleAnchorDateChange}
              onMove={(direction) => setAnchorDate((current) => moveAnchorDate(mode, current, direction))}
              periodTitle={periodTitle} classes={classes} classCode={classCode} onClassChange={setClassCode} />
          )}

          {classLoading && !childrenLoading && <div className="lms-report__empty" role="status">수강 강좌를 불러오는 중입니다.</div>}
          {!classLoading && !classes.length && !error && (!isParent || studentId) && <div className="lms-report__empty">수강 중인 강좌가 없습니다.</div>}
          {error && <div className="lms-report__error" role="alert">{error}
            <button type="button" onClick={() => setRetryCount((value) => value + 1)}>다시 확인</button>
          </div>}
          {reportLoading && <div className="lms-report__empty" role="status">학습 상태를 불러오는 중입니다.</div>}

          {!!classes.length && !reportLoading && !error && (
            <div className="lms-report__content">
              <SummaryGrid summary={summary} />
              <AttendanceCalendar mode={mode} anchorDate={anchorDate} rows={rows} />
              <LearningDetails rows={rows} />
              <ReportInsights summary={summary} rows={rows} />
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default LmsLearningReport;
